import {
  ANNUAL_PORTFOLIO_NEEDS,
  CANCELLED_FRANCE_MONTHS,
  DOMAIN_PORTFOLIO_PREFIX,
  FRANCE_MONTHS,
  MONTHS,
  MONTH_END,
  PPA_INPUTS,
} from "./complianceGroupsFixture";
import type {
  CertificateLot,
  DomainKey,
  PpaInput,
  PpaKey,
} from "./complianceGroupsTypes";

export const francePortfolioName = (monthIndex: number): string =>
  `${DOMAIN_PORTFOLIO_PREFIX.fr} · ${MONTHS[monthIndex]} 2026`;

export const annualPortfolioName = (key: DomainKey): string =>
  `${DOMAIN_PORTFOLIO_PREFIX[key]} · 2026`;

const pad2 = (n: number): string => String(n).padStart(2, "0");

const STATUS_RANK = { Available: 0, Cancelled: 1 } as const;

/** Available lots first (what can still be acted on), then by production month. */
export const sortLots = (lots: CertificateLot[]): CertificateLot[] =>
  [...lots].sort(
    (a, b) =>
      STATUS_RANK[a.status] - STATUS_RANK[b.status] ||
      a.month.localeCompare(b.month)
  );

let lotCounter = 0;
const makeLot = (
  ppa: PpaInput,
  monthIndex: number,
  device: string,
  count: number,
  portfolio: string,
  domainKey: DomainKey
): CertificateLot => {
  const cancelled =
    domainKey === "fr" && CANCELLED_FRANCE_MONTHS.has(monthIndex);
  lotCounter += 1;
  return {
    id: `${ppa.key}-${pad2(monthIndex + 1)}-${lotCounter}`,
    ppaKey: ppa.key,
    n: count,
    tech: ppa.tech,
    month: `2026-${pad2(monthIndex + 1)}`,
    status: cancelled ? "Cancelled" : "Available",
    portfolio,
    domainKey,
    cc: ppa.cc,
    registry: ppa.registry,
    registryCode: ppa.registryCode,
    device,
    deadline: `2027-${pad2(monthIndex + 1)}-${MONTH_END[monthIndex]}`,
  };
};

/**
 * One EECS pool. A certificate is allocated the moment it is delivered, in portfolio priority
 * order: France's twelve monthly portfolios first (production month must equal consumption
 * month there), then the annual portfolios in ANNUAL_PORTFOLIO_NEEDS order. UK REGOs are a
 * separate pool and land in the UK annual portfolio only.
 */
export const allocateLots = (): CertificateLot[] => {
  lotCounter = 0;
  const lots: CertificateLot[] = [];
  // Declaration order: France's own agreement first, then the German ones, Sweden, Spain.
  const pool = Object.values(PPA_INPUTS).filter((p) => p.group === "eecs");
  const annual = ANNUAL_PORTFOLIO_NEEDS.map((a) => ({ ...a }));
  let annualIndex = 0;

  for (let m = 0; m < 12; m++) {
    let franceNeed = FRANCE_MONTHS[m].consumption;
    for (const ppa of pool) {
      for (const device of ppa.devices) {
        if (m >= device.deliveredThrough) continue;
        let left = device.actual[m];
        while (left > 0) {
          if (franceNeed > 0) {
            const take = Math.min(left, franceNeed);
            lots.push(
              makeLot(ppa, m, device.name, take, francePortfolioName(m), "fr")
            );
            franceNeed -= take;
            left -= take;
            continue;
          }
          while (annualIndex < annual.length && annual[annualIndex].need <= 0)
            annualIndex++;
          if (annualIndex >= annual.length) break;
          const target = annual[annualIndex];
          const take = Math.min(left, target.need);
          lots.push(
            makeLot(
              ppa,
              m,
              device.name,
              take,
              annualPortfolioName(target.key),
              target.key
            )
          );
          target.need -= take;
          left -= take;
        }
      }
    }
  }

  const uk = PPA_INPUTS.uk;
  for (let m = 0; m < 12; m++) {
    for (const device of uk.devices) {
      if (m >= device.deliveredThrough) continue;
      lots.push(
        makeLot(
          uk,
          m,
          device.name,
          device.actual[m],
          annualPortfolioName("uk"),
          "uk"
        )
      );
    }
  }
  return sortLots(lots);
};

/** The allocation never changes during the demo, so compute it once. */
export const LOTS: CertificateLot[] = allocateLots();

export const lotsForDomain = (
  lots: CertificateLot[],
  key: DomainKey
): CertificateLot[] => sortLots(lots.filter((l) => l.domainKey === key));

export const lotsForPpa = (
  lots: CertificateLot[],
  key: PpaKey
): CertificateLot[] => sortLots(lots.filter((l) => l.ppaKey === key));

export const lotsForPortfolio = (
  lots: CertificateLot[],
  portfolio: string
): CertificateLot[] => sortLots(lots.filter((l) => l.portfolio === portfolio));
